import { useEffect, useState } from "react";
import { Link2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

const NONE = "__none__";
const EXTERNAL = "__external__";

function StatusBadge({ row, t }) {
    if (row.branch_id) {
        return (
            <Badge className="bg-[color:rgba(22,163,74,0.12)] text-[color:var(--success)] border-0 text-[10px]">
                {t("cc_map_mapped")}
            </Badge>
        );
    }
    if (row.external) {
        return (
            <Badge variant="secondary" className="bg-[color:rgba(224,222,217,0.9)] text-[color:var(--ink-muted)] border-0 text-[10px]">
                {t("cc_map_external")}
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="border-[color:var(--brand-orange)] text-[color:var(--brand-brown)] text-[10px]">
            {t("erp_pl_not_mapped")}
        </Badge>
    );
}

export default function CostCenterMapDialog({ open, onOpenChange }) {
    const { t, tn, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        api
            .get("/financials/cost-center-map")
            .then((r) => {
                if (!active) return;
                setRows(r.data.map || []);
                setBranches(r.data.branches || []);
            })
            .catch(() => active && toast.error(t("error_generic")))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const valueOf = (row) => (row.branch_id ? row.branch_id : row.external ? EXTERNAL : NONE);

    const onChange = async (name, value) => {
        const branch_id = value === NONE || value === EXTERNAL ? null : value;
        const external = value === EXTERNAL;
        const prev = rows;
        setRows((rs) => rs.map((r) => (r.cost_center_name === name ? { ...r, branch_id, external } : r)));
        try {
            await api.put("/financials/cost-center-map", { cost_center_name: name, branch_id, external });
            toast.success(t("cc_map_saved"));
        } catch (e) {
            setRows(prev);
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={OPS.ccMap.dialog} className="max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-thin">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Link2 className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        {t("cc_map_title")}
                    </DialogTitle>
                    <DialogDescription>{t("cc_map_desc")}</DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-10 text-[color:var(--ink-muted)]">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : !rows.length ? (
                    <p className="py-8 text-center text-sm text-[color:var(--ink-muted)]">{t("cc_map_empty")}</p>
                ) : (
                    <div className="rounded-lg border border-[color:var(--border-soft)] overflow-x-auto">
                        <Table data-testid={OPS.ccMap.table}>
                            <TableHeader>
                                <TableRow className="bg-[color:rgba(224,222,217,0.92)] hover:bg-[color:rgba(224,222,217,0.92)]">
                                    <TableHead className="text-start whitespace-nowrap">{t("erp_pl_cc")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("erp_pl_branch")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("cc_map_status")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.cost_center_name} data-testid={OPS.ccMap.row(row.cost_center_name)}>
                                        <TableCell className="font-medium text-[color:var(--ink)] whitespace-nowrap">
                                            {row.cost_center_name}
                                        </TableCell>
                                        <TableCell>
                                            <Select value={valueOf(row)} onValueChange={(v) => onChange(row.cost_center_name, v)}>
                                                <SelectTrigger
                                                    data-testid={OPS.ccMap.select(row.cost_center_name)}
                                                    className="w-[220px] surface"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={NONE}>
                                                        <span className="text-[color:var(--ink-muted)]">{t("cc_map_not_set")}</span>
                                                    </SelectItem>
                                                    <SelectItem value={EXTERNAL}>{t("cc_map_external")}</SelectItem>
                                                    {branches.map((b) => (
                                                        <SelectItem key={b.id} value={b.id}>
                                                            {tn(b)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell data-testid={OPS.ccMap.status(row.cost_center_name)}>
                                            <StatusBadge row={row} t={t} lang={lang} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
